import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogPlan from "./DialogPlan";
import { newFeatureDetails, planPrice, planPriceDesc } from "../Dialog/PlanFeatures";
// material-ui
import {
  Box,
  Grid,
  Typography,
  SelectChangeEvent,
  Button,
  Avatar,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import CustomTextField from "../FormFields/CustomTextField";
import {
  IAccountBasicFormInputs,
  ISubscriptions,
} from "../../utils/account.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledInput } from './Dialog.styles';
import axios from "../../utils/axios";
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isAddButton?: boolean;
  data?: any;
}

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
  taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
});

//Custom Dialog Component.
const CustomDialog: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  isAddButton,
  data,
}) => {
  const [goToPlans, setGoToPlans] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [subscriptionOptions, setSubscriptionOptions] = useState<IOptions[]>(
    []
  );
  const [featuresData, setfeaturesData] = useState({});
  const [featuresPrice, setfeaturesPrice] = useState({});
  const [featuresPriceDesc, setfeaturesPriceDesc] = useState({});
  const [industryVerticalOptions, setIndustryVerticalOptions] = useState<
    IOptions[]
  >([]);
  const [selectedIndustryVertical, setSelectedIndustryVertical] = useState('');
  // const [selectedCompanyType, setSelectedCompanyType] = useState("");
  //const [companyTypeOptions, setCompanyTypeOptions] = useState<IOptions[]>([]);
  const { status } = useAppSelector((state) => state.account);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { register } = useForm({});
  const [selectedBusinessType, setSelectedBusinessType] = useState<string[]>([]);

  const businessTypeOptions = [
    'Product',
    'Service',
    'Consulting'
  ];

  //Fetch Subscriptions on Account form load.
  useEffect(() => {
    let isMounted = true;
    // Method to fetch available subscriptions.
    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get<ISubscriptions>(
          "/api/v1/subscription"
        );
        const { subscriptions = [] } = response.data;

        const options = subscriptions.map(({ _id, plan }) => ({
          label: plan.toUpperCase(),
          value: _id,
        }));

        let featureDetails: Array<any> = [];

        subscriptions.map(({ _id, resources }) => {
          let featuesOptions = resources.map(({ action, displayName }) => {
            return action + ' ' + displayName;
          })
          featureDetails.push(featuesOptions);
          return featureDetails
        });
        //console.log(featureDetails)

        setfeaturesData(newFeatureDetails)
        setfeaturesPrice(planPrice)
        setfeaturesPriceDesc(planPriceDesc)


        if (isMounted) {
          setSubscriptionOptions(options);
        }
      } catch (error: any) {
        // Empty catch block.
      }
    };
    fetchSubscriptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // Populate Industry Vertical on Account form load
  useEffect(() => {
    let options = [
      { label: "Aerospace (aircraft manufacturing)", value: "Aerospace (aircraft manufacturing)" },
      { label: "Agriculture", value: "Agriculture" },
      { label: "Chemical (manufacturing)", value: "Chemical (manufacturing)" },
      { label: "Computers", value: "Computers" },
      { label: "Construction", value: "Construction" },
      { label: "Defense", value: "Defense" },
      { label: "Energy (production, distribution)", value: "Energy (production, distribution)" },
      { label: "Entertainment", value: "Entertainment" },
      { label: "Financial", value: "Financial" },
      { label: "Food", value: "Food" },
      { label: "Health Care", value: "Health Care" },
      { label: "Hospitality", value: "Hospitality" },
      { label: "Information", value: "Information" },
      { label: "Manufacturing", value: "Manufacturing" },
      { label: "Mass Media", value: "Mass Media" },
      { label: "Telecommunications", value: "Telecommunications" },
      { label: "Transport", value: "Transport" },
      { label: "Water", value: "Water" },
      { label: "Others", value: "Others" },
    ];
    setIndustryVerticalOptions(options);
  }, []);

  // Populate Company Type on Account form load.
  // useEffect(() => {
  //   let options = [
  //     { label: "Proprietorship", value: "Proprietorship" },
  //     { label: "Partnership", value: "Partnership" },
  //     { label: "Limited Liability Partnership", value: "Limited Liability Partnership" },
  //     { label: "Private Limited Companies", value: "Private Limited Companies" },
  //     { label: "Public Limited Companies", value: "Public Limited Companies" },
  //     { label: "One Person Company", value: "One Person Company" },
  //     { label: "Non Profit Company", value: "Non Profit Company" }
  //   ]
  //   setCompanyTypeOptions(options);
  // }, []);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema),
  });

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('name', data.name);
    form.append('legalName', data.legalName);
    form.append('industryVertical', data.industryVertical);
    form.append('displayName', data.displayName);
    form.append('companyDesc', data.companyDesc);
    form.append('businessType', JSON.stringify(selectedBusinessType));
    form.append('taxIdentificationNumber1', data.taxIdentificationNumber1);
    form.append('taxIdentificationNumber2', data.taxIdentificationNumber2);
    if (selectedFile) {
      form.append('logo', selectedFile);
    }
    if (data.capitalOpeningBalance) {
      form.append('capitalOpeningBalance', data.capitalOpeningBalance);
    }
    const subscriptionPlan = subscriptionOptions.find((s, i) => {
      return tabValue === i;
    });
    if (subscriptionPlan?.value) {
      form.append("subscription", subscriptionPlan.value);
    }
    //onSubmit({...data, ...{subscription: subscriptionPlan?.value, logo: form }});
    onSubmit(form);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<typeof selectedBusinessType>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedBusinessType(
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    let basicInfoAvaliable = true;

    const setInitialData = () => {
      try {
        if (data && basicInfoAvaliable) {
          methods.setValue('name', data.name);
          methods.setValue('displayName', data.displayName);
          methods.setValue('companyDesc', data.companyDesc);
          methods.setValue('website', data.website);
          methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
          methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
          if (data.businessType) setSelectedBusinessType(data.businessType);
          if (data.industryVertical) setSelectedIndustryVertical(data.industryVertical);
          //setSelectedCompanyType(data.companyType);
          setImage(data.logo);
        }
      } catch (error: any) {
        console.log("Dialog catch block called");
      }
    };

    setInitialData();

    return () => {
      basicInfoAvaliable = false;
    };
  }, [data, methods]);

  useEffect(() => {
    if (data && subscriptionOptions.length) {
      let tabIndex = subscriptionOptions.findIndex(x => x.label.toLowerCase() === data.subscription['plan'].toLowerCase());
      setTabValue(tabIndex)
    }
  }, [subscriptionOptions, data]);

  const gotoPlan = () => {
    let brangName = methods.getValues('name');
    let legalName = methods.getValues('legalName');
    let taxIdentificationNumber1 = methods.getValues('taxIdentificationNumber1');
    let taxIdentificationNumber2 = methods.getValues('taxIdentificationNumber2');
    if(!brangName) methods.trigger("name");
    if(!legalName) methods.trigger("legalName");
    if(!taxIdentificationNumber1) methods.trigger("taxIdentificationNumber1");
    if(!taxIdentificationNumber2) methods.trigger("taxIdentificationNumber2");
    if (brangName && legalName && taxIdentificationNumber1 && taxIdentificationNumber2) {
      setGoToPlans(true);
    }else{
      methods.handleSubmit(innerSubmit);
    }
  };


  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={(!goToPlans)? "xs" : "md"}
      onClose={isAddButton ? onClose : undefined}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
        >
          <DialogTitle>
            <Box sx={{ display: "flex", mt: 2, marginLeft:(!goToPlans) ? "-4px": "14px"}}>
              <Typography variant="h5">{title}</Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Grid container display="flex" justifyContent="left" spacing={0} >
              {!goToPlans ? (
                <>
                  <Grid item xs={12} md={12} sm={12}>
                    <Stack gap={12} direction="row" alignItems="center">
                      <div>
                        <div style={{ position: "relative", height:"100%", display:"flex", flexDirection:'column', alignItems:"center", justifyContent:"center" }}>
                        <div className="file-name">
                          {image && (
                            <Avatar
                              alt={"Picture"}
                              imgProps={{ referrerPolicy: "no-referrer" }}
                              src={image}
                              style={{
                                width: "100px",
                                height: "100px",
                                marginBottom:"20px",
                                objectFit: "contain",
                              }}
                              variant='square'
                              sizes={"lg"}
                            />
                          )}
                        </div>
                        
                        <label htmlFor="logo">
                          <StyledInput
                            accept="image/*"
                            id="logo"
                            type="file"
                            onChange={handleSelectFile}
                          />
                          <Button
                            variant="outlined"
                            className="btn-choose"
                            component="span"
                          >
                            Choose Logo
                          </Button>
                        </label>
                      </div>
                      
                        <CustomTextField
                          label="Brand Name"
                          name="name"
                          required
                          variant="standard"
                        />
                        <CustomTextField
                          label="Display Name"
                          name="displayName"
                          variant="standard"
                        />
                        <CustomTextField
                          label="Legal Name"
                          name="legalName"
                          required
                          variant="standard"
                        />
                        <CustomTextField
                          label="Company Description"
                          name="companyDesc"
                          variant="standard"
                          multiline
                          rows={2}
                        />
                        {/* <CustomSelect
                        name="companyType"
                        label="Company Type"
                        id="companyType"
                        variant="standard"
                        options={companyTypeOptions}
                        value={selectedCompanyType || ""}
                        onChange={(event: SelectChangeEvent<any>) => {
                          setSelectedCompanyType(event.target.value);
                        }}
                      /> */}
                        <CustomTextField label="Tax Identification #1 (PAN for India)" required name="taxIdentificationNumber1" variant="standard" />
                        <CustomTextField label="Tax Identification #2 (GST for India)" required name="taxIdentificationNumber2" variant="standard" />
                        <CustomSelect
                          labelId="industryVertical"
                          name="industryVertical"
                          label="Industry Vertical"
                          id="industryVertical"
                          variant="standard"
                          options={industryVerticalOptions}
                          value={selectedIndustryVertical}
                          onChange={(event: SelectChangeEvent<any>) => {
                            setSelectedIndustryVertical(event.target.value);
                          }}
                        />
                        <FormControl
                          fullWidth
                          sx={{ mt: 2, mb: 4 }}
                          variant="standard"
                        >
                          <InputLabel id={"multiple-checkbox-label"}>
                            Business Type
                          </InputLabel>
                          <Select
                            labelId="multiple-checkbox-label"
                            id="businessType"
                            multiple
                            value={selectedBusinessType}
                            onChange={(event: SelectChangeEvent<any>) => {
                              handleSelectChange(event);
                            }}
                            renderValue={(selected: any) => selected.join(", ")}
                          >
                            {businessTypeOptions.map((name) => (
                              <MenuItem key={name} value={name}>
                                <Checkbox
                                  checked={
                                    selectedBusinessType.indexOf(name) > -1
                                  }
                                />
                                <ListItemText primary={name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <CustomTextField
                          label="Capital Opening Balance"
                          name="capitalOpeningBalance"
                          type="number"
                          variant="standard"
                          inputProps={{ min: 0 }}
                        />
                      </div>
                    </Stack>
                  </Grid>
                  <Grid
                    container
                    display="flex"
                    justifyContent="flex-end"
                    spacing={3}
                    sx={{ mt: 1 }}
                    gap={0}
                  >
                    {isAddButton && (
                      <Button
                        // style={{ marginRight: 50 }}
                        variant="outlined"
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          methods.reset();
                          onClose();
                        }}
                      >
                        Cancel
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={gotoPlan}
                    >
                      Next
                    </Button>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={12} sm={12}>
                  <Typography sx={{marginBottom:'-40px',marginLeft:'15px',marginTop:'-5px'}}>Select a plan. You can change the plan at any time through settings.</Typography>
                  <Grid item xs={12} md={12} sm={12} sx={{ mt: 5, backgroundColor: "#fafafb", borderRadius: "8px", height: '250px' }}>
                    <DialogPlan
                      featuresData={featuresData}
                      featurePrice={featuresPrice}
                      featurePriceDesc={featuresPriceDesc}
                      subscriptionOptions={subscriptionOptions}
                      tabValue={tabValue}
                      handleTabs={(val: any) => handleTabChange(val)}
                      register={register}
                    />
                  </Grid>

                  <Grid
                    container
                    justifyContent="right"
                    spacing={3}
                    sx={{ mr: "100%", mt: '300px' }}
                  >
                    <Button
                      style={{ marginRight: "10px" }}
                      variant="outlined"
                      onClick={() => setGoToPlans(false)}
                    >
                      Back
                    </Button>

                    <LoadingButton
                      type="submit"
                      loading={status === ApiStatus.LOADING}
                      variant="contained"
                      color="secondary"
                      style={{ marginRight:"15px"}}
                    >
                      {data ? "Update" : "Create"}
                    </LoadingButton>
                  </Grid>
                </Grid>
                
              )}
            </Grid>
            
            {children}
          </DialogContent>
        </Box>
      </FormProvider>
    </Dialog>
  );
};

export default CustomDialog;
